import React, { type RefObject, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { Platform, type ViewInstance } from 'react-native';

import { Navbar } from '@garnish/navigation';

import { useGroupOrderingContent, useGroupOrderIntroState } from '@order/features-group-ordering';
import { useTelemetry } from '@order/Telemetry';

import { GroupOrderIntroModal } from '../../../GroupOrderIntroModal';
import { NAVBAR_GLASS_FILL_OPACITY } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarStartMenu = (props: Props) => {
  const { isLiquidGlassEnabled, returnFocusRef, visible, onClose } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  const router = useRouter();
  const { mappedContent: groupOrderingContent } = useGroupOrderingContent();

  // ─── Group Order Intro ─────────────────────────────────────────────────────

  const handleIntroComplete = useCallback(() => {
    router.navigate('/group-ordering/create');
  }, [router]);

  const handleIntroShow = useCallback(() => {
    track('group_ordering.intro.shown');
  }, [track]);

  const {
    visible: isIntroVisible,
    show: showIntro,
    hide: hideIntro,
    dismiss: dismissIntro,
  } = useGroupOrderIntroState({
    onShow: handleIntroShow,
    onComplete: handleIntroComplete,
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleViewMenuPress = useCallback(() => {
    onClose();
    router.navigate('/menu');
  }, [onClose, router]);

  const handleCreateYourOwnPress = useCallback(() => {
    onClose();
    router.navigate({ pathname: '/menu', params: { category: 'custom' } });
  }, [onClose, router]);

  const handleStartGroupOrderPress = useCallback(() => {
    track('group_ordering.entry_point.tapped');
    onClose();
    showIntro();
  }, [onClose, showIntro, track]);

  const handleIntroPrimaryCtaPress = useCallback(() => {
    track('group_ordering.intro.primary_cta.tapped');
  }, [track]);

  const handleIntroSecondaryCtaPress = useCallback(() => {
    track('group_ordering.intro.secondary_cta.tapped');
    track('group_ordering.intro.dismissed');

    dismissIntro();
  }, [dismissIntro, track]);

  const handleIntroStartOrder = useCallback(() => {
    track('group_ordering.intro.primary_cta.tapped');
    track('group_ordering.intro.dismissed');

    hideIntro();
  }, [hideIntro, track]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      {visible ? (
        <Navbar.Menu.Container
          accessibilityLabel={formatMessage(messages.startMenu)}
          glassFillOpacity={NAVBAR_GLASS_FILL_OPACITY}
          isLiquidGlassEnabled={isLiquidGlassEnabled}
          returnFocusRef={returnFocusRef}
          withBlur={Platform.OS === 'ios'}
          onClose={onClose}
        >
          <Navbar.Menu.List>
            <Navbar.Menu.ListItem
              autoFocus
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              onPress={handleViewMenuPress}
            >
              {formatMessage(messages.viewMenu)}
            </Navbar.Menu.ListItem>

            <Navbar.Menu.ListItem
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              onPress={handleCreateYourOwnPress}
            >
              {formatMessage(messages.createYourOwn)}
            </Navbar.Menu.ListItem>

            <Navbar.Menu.ListItem
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              onPress={handleStartGroupOrderPress}
            >
              {groupOrderingContent.entryPointCtaLabel}
            </Navbar.Menu.ListItem>
          </Navbar.Menu.List>
        </Navbar.Menu.Container>
      ) : null}

      <GroupOrderIntroModal
        visible={isIntroVisible}
        content={groupOrderingContent}
        onPrimaryCtaPress={handleIntroPrimaryCtaPress}
        onSecondaryCtaPress={handleIntroSecondaryCtaPress}
        onStartOrder={handleIntroStartOrder}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isLiquidGlassEnabled: boolean;
  returnFocusRef: RefObject<ViewInstance | null>;
  visible: boolean;
  onClose: () => void;
};
